<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { getCookiesPolicyPath } from "@core/schema/paths/getCookiesPolicyPath.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getTermsOfServicePath } from "@core/schema/paths/getTermsOfServicePath.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { expectErrorMessage } from "@core/utils/expectErrorMessage.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import SignInWithGoogle from "./SignInWithGoogle.svelte";
	import translations from "./SignUp.translations.json" with { type: "json" };

	const signUp = createPromiseWithStatus(Auth.signUp.bind(Auth));
	const isDesktop = createReactiveBreakpoint(">md");
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	function validatePasswordsMatch(form: HTMLFormElement): void {
		const passwordAgain = form.elements.namedItem("passwordAgain") as HTMLInputElement;
		const password = form.elements.namedItem("password") as HTMLInputElement;

		if (passwordAgain.value !== password.value && passwordAgain.value.length > 0 && password.value.length > 0) {
			passwordAgain.setCustomValidity(translate("mismatch"));
			password.setCustomValidity(translate("mismatch"));
		} else {
			passwordAgain.setCustomValidity("");
			password.setCustomValidity("");
			passwordAgain.reportValidity();
			password.reportValidity();
		}
	}

	async function handleSignUp(_event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		validatePasswordsMatch(form);
		if (!form.reportValidity()) {
			return;
		}
		const formData = new FormData(form);
		await signUp.call({
			email: formData.get("email") as string,
			password: formData.get("password") as string,
			locale: tenant.locale,
			subscribeToNewsletter: formData.get("newsletterConsent") === "on",
			gdprApprovedAt: new Date().toISOString(),
		});
		Auth.openAuthenticationModal(AuthenticationModalKind.RegisterSuccess, {});
	}

	const error = $derived(
		signUp.value.status === "error" &&
			expectErrorMessage(
				signUp.value.error,
				tenant.locale,
				{
					UserAlreadyExistsException: translate("userAlreadyExists"),
				},
				translate("unspecifiedError"),
			),
	);
</script>

<Form
	class="flex flex-col items-stretch gap-4 mt-4.5 md:mt-8"
	onsubmit={handleSignUp}
	onchange={(event: Event & { currentTarget: EventTarget & HTMLFormElement }): void => {
		validatePasswordsMatch(event.currentTarget);
	}}
>
	<SignInWithGoogle />
	<div class="text-fcrgray text-sm/6 md:text-base text-center">
		<Formatted text={translate("or")} />
	</div>
	<Input autocomplete="email" minlength={7} required placeholder={translate("email")} name="email" type="email" />
	<Input
		autocomplete="new-password"
		minlength={7}
		required
		placeholder={translate("password")}
		type="password"
		name="password"
	/>
	<Input
		autocomplete="new-password"
		minlength={7}
		required
		placeholder={translate("passwordConfirmation")}
		type="password"
		name="passwordAgain"
	/>
	<LabelledInput type="checkbox" name="tosConsent" required wrapperClass="ml-1">
		<Formatted
			text={translate("tosConsent", { tosUrl: getTermsOfServicePath(tenant), privacyUrl: getGdprPath(tenant) })}
		/>
	</LabelledInput>
	<LabelledInput type="checkbox" name="newsletterConsent" wrapperClass="ml-1">
		<Formatted text={translate("newsletterConsent")} />
	</LabelledInput>
	<Button
		loading={signUp.value.status === "loading"}
		variant={ButtonVariant.Dark}
		size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
	>
		<Formatted text={translate("register")} />
	</Button>

	{#if error}
		<p class="text-sm text-fcrerror mt-2 [&_a]:text-fcrbrown">
			<Formatted text={error} />
		</p>
	{/if}
</Form>
<div class="mt-6 text-base md:text-1.5xl text-center font-medium">
	<Formatted text={translate("alreadyHave")} />
	<button
		class="text-fcrbrown underline"
		type="button"
		onclick={(): void => {
			Auth.openAuthenticationModal(AuthenticationModalKind.SignIn, {});
		}}
	>
		<Formatted text={translate("signIn")} />
	</button>
</div>
